import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, Loader2, Eye, EyeOff, Check } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password, remember);
      toast.success(`Welcome back, ${user.name}!`);
      
      // Redirect based on role
      if (user.role === "admin") {
        navigate("/admin");
      } else if (user.onboarding?.final_confirmed) {
        navigate("/dashboard");
      } else {
        navigate(user.guest_type === "retreat" ? "/retreat-welcome" : "/onboarding");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1] flex flex-col savi-bg-pattern">
      {/* Header */}
      <header className="p-6">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#2E2A26]/60 hover:text-[#2E2A26] transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Savi</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            {/* Savi Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" 
                alt="Savi Coliving" 
                className="h-16 w-auto"
              />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-[#2E2A26] mb-2 tracking-wide">
              Welcome Back
            </h1>
            <p className="text-[#2E2A26]/60">
              Sign in to continue your journey
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-sm border border-stone-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#2E2A26] font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2A26]/40" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-12 h-14 rounded-xl border-stone-200 bg-[#FBF7F1] focus:ring-2 focus:ring-[#C85A38]/20 focus:border-[#C85A38]"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#2E2A26] font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2A26]/40" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-12 pr-12 h-14 rounded-xl border-stone-200 bg-[#FBF7F1] focus:ring-2 focus:ring-[#C85A38]/20 focus:border-[#C85A38]"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2E2A26]/40 hover:text-[#2E2A26]/70"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="peer sr-only"
                  data-testid="remember-me-checkbox"
                />
                <span className="w-5 h-5 rounded-md border-2 border-stone-300 flex items-center justify-center transition-colors peer-checked:bg-[#C85A38] peer-checked:border-[#C85A38] peer-focus-visible:ring-2 peer-focus-visible:ring-[#C85A38]/30" aria-hidden="true">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </span>
                <span className="text-sm text-[#2E2A26]/70">Keep me signed in on this device</span>
              </label>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#C85A38] hover:bg-[#B85540] text-white rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 btn-press"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            {/* Google sign-in */}
            <div className="mt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-stone-200" />
                <span className="text-xs text-[#2E2A26]/40">or</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>
              <button
                type="button"
                onClick={() => {
                  const redirectUrl = `${window.location.origin}/dashboard`;
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full h-14 bg-white border border-stone-200 hover:border-stone-300 hover:bg-stone-50 rounded-full flex items-center justify-center gap-3 text-[#2E2A26] font-medium transition-colors"
                data-testid="google-login-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              <p className="text-[#2E2A26]/40 text-[11px] text-center mt-2">
                Use the same email Steve set your account up with.
              </p>
            </div>

            <div className="mt-6 text-center space-y-4">
              <button
                type="button"
                onClick={() => toast.info("Please contact us at hola@savicoliving.com")}
                className="text-[#C85A38] hover:underline font-medium text-sm"
                data-testid="forgot-details-link"
              >
                Forgot your login details?
              </button>
              <p className="text-[#2E2A26]/40 text-xs">
                Don't have an account? Contact us to book your stay.
              </p>
            </div>
          </div>

          {/* Contact info */}
          <div className="mt-6 text-center text-sm text-[#2E2A26]/50">
            <p>Questions? Email hola@savicoliving.com</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
